import { addQueryParams } from '../lib/route.utils';
import { apiFormRequest, apiRequest } from './requests.helpers';

export const users = {
  getCurrent: () => apiRequest('GET', '/me'),
  submitFirstTimeSetup: (body) => apiRequest('POST', '/me', { body }),
  getPreferences: (userName, resource) => apiRequest('GET', `/api/preferences/${userName}/${resource}`),
  upsertPreferences: (userName, resource, body) =>
    apiRequest('PUT', `/api/preferences/${userName}/${resource}`, { body }),
  refreshToken: (refreshToken, loginMethod) =>
    apiRequest('POST', '/me/refresh', { body: { refreshToken, loginMethod } })
};

export const offerings = {
  search: (urlConfig) => {
    return apiRequest('GET', addQueryParams('/api/offerings/all', urlConfig));
  },
  searchRestricted: (urlConfig) => {
    return apiRequest('GET', addQueryParams('/api/offerings', urlConfig));
  },
  getById: async (id) => {
    return apiRequest('GET', `/api/offerings/${id}`);
  },
  add: async (body) => apiFormRequest('POST', '/api/offerings', body, { multipleFilesKey: 'images' }),
  addImage: async (id, body) => apiFormRequest('POST', `/api/offerings/${id}/images`, body),
  update: async (id, body) => apiRequest('PATCH', `/api/offerings/${id}`, { body }),
  delete: async (id) => apiRequest('DELETE', `/api/offerings/${id}`),
  deleteImage: async (id, name) => apiRequest('DELETE', `/api/offerings/${id}/images/${name}`)
};

export const themes = {
  search: (urlConfig) => apiRequest('GET', addQueryParams('/api/themes/search', urlConfig)),
  getAll: () => apiRequest('GET', '/api/themes'),
  getAllThemesForOfferings: () => apiRequest('GET', '/api/themes/offerings'),
  add: (body) => apiRequest('POST', '/api/themes', { body }),
  update: (id, body) => apiRequest('PATCH', `/api/themes/${id}`, { body }),
  remove: (id) => apiRequest('DELETE', `/api/themes/${id}`)
};

const dossiersPath = '/api/dossiers';
export const dossiers = {
  create: (type, body) => apiFormRequest('POST', `${dossiersPath}/${type}`, body, { multipleFilesKey: 'attachments' }),
  search: (urlConfig) => {
    return apiRequest('GET', addQueryParams(dossiersPath, urlConfig));
  },
  getById: (id) => apiRequest('GET', `${dossiersPath}/${id}`),
  update: (id, body) => apiRequest('PATCH', `${dossiersPath}/${id}`, { body }),
  assignedUsersChange: (id, body) => apiRequest('POST', `${dossiersPath}/${id}/assigned`, { body }),
  statusChange: (id, body) => apiRequest('POST', `${dossiersPath}/${id}/status`, { body }),
  relateDossiers: (id, body) => apiRequest('POST', `${dossiersPath}/${id}/related`, { body }),
  removeRelations: (id, urlConfig) => apiRequest('DELETE', addQueryParams(`${dossiersPath}/${id}/related`, urlConfig))
};

const getDossierOverviewPath = (dossierId) => `/api/dossiers/${dossierId}/overview`;
export const dossierOverview = {
  update: (dossierId, body) => apiRequest('PATCH', getDossierOverviewPath(dossierId), { body })
};

const getDossierActivityPath = (id) => `/api/dossiers/${id}/activities`;
export const activities = {
  dossier: (dossierId) => apiRequest('GET', getDossierActivityPath(dossierId))
};

const getDossierCommentsPath = (id) => `/api/dossiers/${id}/comments`;
export const comments = {
  searchMentionUsers: (dossierId, name) =>
    apiRequest('GET', `${getDossierCommentsPath(dossierId)}/mention-users?name=${name}`),
  dossier: (dossierId) => apiRequest('GET', getDossierCommentsPath(dossierId)),
  add: (dossierId, body) => apiRequest('POST', getDossierCommentsPath(dossierId), { body }),
  update: (dossierId, commentId, body) =>
    apiRequest('PATCH', `${getDossierCommentsPath(dossierId)}/${commentId}`, { body }),
  delete: (dossierId, commentId) => apiRequest('DELETE', `${getDossierCommentsPath(dossierId)}/${commentId}`),
  pin: (dossierId, commentId) => apiRequest('POST', `${getDossierCommentsPath(dossierId)}/${commentId}/pin`),
  unpin: (dossierId, commentId) => apiRequest('POST', `${getDossierCommentsPath(dossierId)}/${commentId}/unpin`)
};

const getClarificationsPath = (dossierId) => `/api/dossiers/${dossierId}/request-clarifications`;
export const clarifications = {
  add: (id) => apiRequest('POST', getClarificationsPath(id)),
  update: (id, clarificationId, body) =>
    apiRequest('PATCH', `${getClarificationsPath(id)}/${clarificationId}`, { body }),
  delete: (id, clarificationId) => apiRequest('DELETE', `${getClarificationsPath(id)}/${clarificationId}`),
  addStakeholder: (id, clarificationId, body) =>
    apiRequest('POST', `${getClarificationsPath(id)}/${clarificationId}/involved`, { body }),
  updateStakeholder: (id, clarificationId, involvedId, body) =>
    apiRequest('PATCH', `${getClarificationsPath(id)}/${clarificationId}/involved/${involvedId}`, { body }),
  removeStakeholder: (id, clarificationId, involvedId) =>
    apiRequest('DELETE', `${getClarificationsPath(id)}/${clarificationId}/involved/${involvedId}`),
  approve: (id, clarificationId, body) =>
    apiRequest('POST', `${getClarificationsPath(id)}/${clarificationId}/approve`, { body })
};

const getCasetafelsPath = (dossierId) => `/api/dossiers/${dossierId}/case-tables`;
export const casetafels = {
  add: (id, body) => apiRequest('POST', getCasetafelsPath(id), { body }),
  update: (id, casetafelId, body) => apiRequest('PATCH', `${getCasetafelsPath(id)}/${casetafelId}`, { body }),
  delete: (id, casetafelId) => apiRequest('DELETE', `${getCasetafelsPath(id)}/${casetafelId}`),
  exportAnswers: (id, casetafelId) => apiRequest('GET', `${getCasetafelsPath(id)}/${casetafelId}/answers-export`),
  approve: (id, casetafelId, body) => apiRequest('POST', `${getCasetafelsPath(id)}/${casetafelId}/approve`, { body }),
  reject: (id, casetafelId, body) => apiRequest('POST', `${getCasetafelsPath(id)}/${casetafelId}/reject`, { body }),
  addStakeholder: (id, casetafelId, body) =>
    apiRequest('POST', `${getCasetafelsPath(id)}/${casetafelId}/involved`, { body }),
  updateStakeholder: (id, casetafelId, involvedId, body) =>
    apiRequest('PATCH', `${getCasetafelsPath(id)}/${casetafelId}/involved/${involvedId}`, { body }),
  removeStakeholder: (id, casetafelId, involvedId) =>
    apiRequest('DELETE', `${getCasetafelsPath(id)}/${casetafelId}/involved/${involvedId}`)
};

const getProcedurePath = (dossierId) => `/api/dossiers/${dossierId}/procedure`;
export const procedure = {
  update: (dossierId, body) => apiRequest('PATCH', getProcedurePath(dossierId), { body }),
  addGoal: (dossierId) => apiRequest('POST', `${getProcedurePath(dossierId)}/goals`),
  updateGoal: (dossierId, id, body) => apiRequest('PATCH', `${getProcedurePath(dossierId)}/goals/${id}`, { body }),
  deleteGoal: (dossierId, id) => apiRequest('DELETE', `${getProcedurePath(dossierId)}/goals/${id}`),
  addPart: (dossierId) => apiRequest('POST', `${getProcedurePath(dossierId)}/parts`),
  updatePart: (dossierId, id, body) => apiRequest('PATCH', `${getProcedurePath(dossierId)}/parts/${id}`, { body }),
  deletePart: (dossierId, id) => apiRequest('DELETE', `${getProcedurePath(dossierId)}/parts/${id}`),
  addGoalIndicator: (dossierId, goalId) =>
    apiRequest('POST', `${getProcedurePath(dossierId)}/goals/${goalId}/indicators`),
  updateGoalIndicator: (dossierId, goalId, id, body) =>
    apiRequest('PATCH', `${getProcedurePath(dossierId)}/goals/${goalId}/indicators/${id}`, { body }),
  deleteGoalIndicator: (dossierId, goalId, id) =>
    apiRequest('DELETE', `${getProcedurePath(dossierId)}/goals/${goalId}/indicators/${id}`),
  addIndicatorMeasureMoment: (dossierId, goalId, indicatorId, body) =>
    apiRequest('POST', `${getProcedurePath(dossierId)}/goals/${goalId}/indicators/${indicatorId}/measure-moments`, {
      body
    }),
  updateIndicatorMeasureMoment: (dossierId, goalId, indicatorId, measureMomentId, body) =>
    apiRequest(
      'PATCH',
      `${getProcedurePath(dossierId)}/goals/${goalId}/indicators/${indicatorId}/measure-moments/${measureMomentId}`,
      {
        body
      }
    ),
  deleteIndicatorMeasureMoment: (dossierId, goalId, indicatorId, measureMomentId) =>
    apiRequest(
      'DELETE',
      `${getProcedurePath(dossierId)}/goals/${goalId}/indicators/${indicatorId}/measure-moments/${measureMomentId}`
    ),
  addStakeholder: (id, body) => apiRequest('POST', `${getProcedurePath(id)}/involved`, { body }),
  updateStakeholder: (id, involvedId, body) =>
    apiRequest('PATCH', `${getProcedurePath(id)}/involved/${involvedId}`, { body }),
  removeStakeholder: (id, involvedId) => apiRequest('DELETE', `${getProcedurePath(id)}/involved/${involvedId}`)
};

const getContactmomentPath = (dossierId) => `/api/dossiers/${dossierId}/contact-moments`;
export const contactmoments = {
  add: (id, body) => apiRequest('POST', getContactmomentPath(id), { body }),
  update: (id, contactmomentId, body) =>
    apiRequest('PATCH', `${getContactmomentPath(id)}/${contactmomentId}`, { body }),
  delete: (id, contactmomentId) => apiRequest('DELETE', `${getContactmomentPath(id)}/${contactmomentId}`),
  addStakeholder: (id, contactmomentId, body) =>
    apiRequest('POST', `${getContactmomentPath(id)}/${contactmomentId}/involved`, { body }),
  updateStakeholder: (id, contactmomentId, involvedId, body) =>
    apiRequest('PATCH', `${getContactmomentPath(id)}/${contactmomentId}/involved/${involvedId}`, { body }),
  deleteStakeholder: (id, contactmomentId, involvedId) =>
    apiRequest('DELETE', `${getContactmomentPath(id)}/${contactmomentId}/involved/${involvedId}`)
};

export const attachments = {
  getForDossier: (id) => apiRequest('GET', `/api/dossiers/${id}/attachments`),
  uploadForDossier: (id, body) => apiFormRequest('POST', `/api/dossiers/${id}/attachments`, body),
  updateForDossier: (id, attachmentId, body) =>
    apiRequest('PATCH', `/api/dossiers/${id}/attachments/${attachmentId}`, { body }),
  deleteForDossier: (id, attachmentId) => apiRequest('DELETE', `/api/dossiers/${id}/attachments/${attachmentId}`)
};

export const forms = {
  getForCaseTable: async (dossierType) => apiRequest('GET', `/api/forms/case-tables/${dossierType}`),
  getForOffering: async () => apiRequest('GET', `/api/forms/offerings`),
  getBySlug: async (slug) => {
    return apiRequest('GET', `/api/forms/${slug}`);
  },
  submit: async (body) => apiFormRequest('POST', '/api/forms', body, { multipleFilesKey: 'attachments' })
};

export const schools = {
  getAll: async () => apiRequest('GET', '/api/schools'),
  search: async (urlConfig) => apiRequest('GET', addQueryParams('/api/schools/search', urlConfig)),
  searchSchools: async (urlConfig) => apiRequest('GET', addQueryParams('/api/schools/search/schools', urlConfig)),
  getAllManagements: async () => apiRequest('GET', '/api/schools/managements'),
  getAllCommunities: async () => apiRequest('GET', '/api/schools/communities'),
  getNets: async (urlConfig) => apiRequest('GET', addQueryParams('/api/schools/nets', urlConfig)),
  getNet: async (netId, urlConfig) => apiRequest('GET', addQueryParams(`/api/schools/nets/${netId}`, urlConfig)),
  getManagements: async (netId, urlConfig) =>
    apiRequest('GET', addQueryParams(`/api/schools/nets/${netId}/managements`, urlConfig)),
  getManagement: async (netId, managementId, urlConfig) =>
    apiRequest('GET', addQueryParams(`/api/schools/nets/${netId}/managements/${managementId}`, urlConfig)),
  getCommunities: async (netId, managementId, urlConfig) =>
    apiRequest('GET', addQueryParams(`/api/schools/nets/${netId}/managements/${managementId}/communities`, urlConfig)),
  getCommunity: async (netId, managementId, communityId, urlConfig) =>
    apiRequest(
      'GET',
      addQueryParams(`/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}`, urlConfig)
    ),
  getSchools: async (netId, managementId, communityId, urlConfig) =>
    apiRequest(
      'GET',
      addQueryParams(
        `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools`,
        urlConfig
      )
    ),
  getSchool: async (netId, managementId, communityId, schoolId, urlConfig) =>
    apiRequest(
      'GET',
      addQueryParams(
        `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}`,
        urlConfig
      )
    ),
  add: async (netId, managementId, communityId, body) =>
    apiRequest('POST', `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools`, {
      body
    }),
  update: async (netId, managementId, communityId, schoolId, body) =>
    apiRequest(
      'PATCH',
      `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}`,
      { body }
    ),
  remove: async (netId, managementId, communityId, schoolId) =>
    apiRequest(
      'DELETE',
      `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}`
    ),
  getBranches: async (netId, managementId, communityId, schoolId, urlConfig) =>
    apiRequest(
      'GET',
      addQueryParams(
        `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/branches`,
        urlConfig
      )
    ),
  getBranch: async (netId, managementId, communityId, schoolId, branchId, urlConfig) =>
    apiRequest(
      'GET',
      addQueryParams(
        `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/branches/${branchId}`,
        urlConfig
      )
    ),
  addBranch: async (netId, managementId, communityId, schoolId, body) =>
    apiRequest(
      'POST',
      `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/branches`,
      { body }
    ),
  updateBranch: async (netId, managementId, communityId, schoolId, branchId, body) =>
    apiRequest(
      'PATCH',
      `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/branches/${branchId}`,
      { body }
    ),
  removeBranch: async (netId, managementId, communityId, schoolId, branchId) =>
    apiRequest(
      'DELETE',
      `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/branches/${branchId}`
    ),
  setSchoolVisible: async (netId, managementId, communityId, schoolId) =>
    apiRequest(
      'POST',
      `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/visible`
    ),
  setSchoolInvisible: async (netId, managementId, communityId, schoolId) =>
    apiRequest(
      'POST',
      `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/invisible`
    ),
  setBranchVisible: async (netId, managementId, communityId, schoolId, branchId) =>
    apiRequest(
      'POST',
      `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/branches/${branchId}/visible`
    ),
  setBranchInvisible: async (netId, managementId, communityId, schoolId, branchId) =>
    apiRequest(
      'POST',
      `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/branches/${branchId}/invisible`
    ),
  getAgodiBranches: async (netId, managementId, communityId, schoolId, branchId, urlConfig) =>
    apiRequest(
      'GET',
      addQueryParams(
        `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/branches/${branchId}/agodi`,
        urlConfig
      )
    ),
  getAgodiBranch: async (netId, managementId, communityId, schoolId, branchId, agodiBranchId) =>
    apiRequest(
      'GET',
      `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/branches/${branchId}/agodi/${agodiBranchId}`
    ),
  updateAgodiBranch: async (netId, managementId, communityId, schoolId, branchId, agodiBranchId, body) =>
    apiRequest(
      'PATCH',
      `/api/schools/nets/${netId}/managements/${managementId}/communities/${communityId}/schools/${schoolId}/branches/${branchId}/agodi/${agodiBranchId}`,
      { body }
    )
};

export const partners = {
  getAll: () => apiRequest('GET', '/api/partners'),
  search: (urlConfig) => apiRequest('GET', addQueryParams('/api/partners/search', urlConfig)),
  add: (body) => apiRequest('POST', '/api/partners', { body }),
  update: (id, updates) => apiRequest('PATCH', `/api/partners/${id}`, { body: updates }),
  remove: (id) => apiRequest('DELETE', `/api/partners/${id}`)
};

export const contacts = {
  getAll: (urlConfig) => apiRequest('GET', addQueryParams('/api/contacts', urlConfig)),
  getWorkedForSchoolInAnotherDossier: (urlConfig) =>
    apiRequest('GET', addQueryParams('/api/contacts/worked-for-school-in-another-dossier', urlConfig)),
  getNotApprovedCount: () => apiRequest('GET', '/api/contacts/not-approved-count'),
  approveBulk: (ids) => apiRequest('POST', '/api/contacts/approve-bulk', { body: { ids } }),
  add: (body) => apiRequest('POST', '/api/contacts', { body }),
  update: (id, body) => apiRequest('PATCH', `/api/contacts/${id}`, { body }),
  remove: (id) => apiRequest('DELETE', `/api/contacts/${id}`),
  createContactRole: (id, body) => apiRequest('POST', `/api/contacts/${id}/roles`, { body }),
  updateContactRole: (id, roleId, body) => apiRequest('PATCH', `/api/contacts/${id}/roles/${roleId}`, { body }),
  removeContactRole: (id, roleId) => apiRequest('DELETE', `/api/contacts/${id}/roles/${roleId}`)
};

export const templates = {
  getAll: (urlConfig) => apiRequest('GET', addQueryParams('/api/templates', urlConfig)),
  getForCategory: (category) => apiRequest('GET', `/api/templates/${category}`),
  add: (body) => apiFormRequest('POST', '/api/templates', body),
  update: (id, body) => apiRequest('PATCH', `/api/templates/${id}`, { body }),
  remove: (id) => apiRequest('DELETE', `/api/templates/${id}`)
};

export const notifications = {
  getForCurrentUser: () => apiRequest('GET', '/api/notifications/me'),
  acknowledge: (body) => apiRequest('POST', '/api/notifications/acknowledge', { body })
};
